import { Dispatch } from 'react';
import { Action } from '@reduxjs/toolkit';
import {
  ILeagueWSConfig,
  IncomingLeagueWSMessage,
  WSConnectionState,
} from './types';
import { API_ORIGIN } from 'app/consts';
import { leagueActions } from 'app/slice';
import { IDLE_RECONNECT_INTERVAL, LIVE_RECONNECT_INTERVAL } from './consts';

class LeagueWebSocketManager {
  private socket: WebSocket | undefined;

  private dispatch: Dispatch<Action> | undefined;

  private connectionState: WSConnectionState = 'CLOSED';

  private config: ILeagueWSConfig | undefined;

  private reconnectInterval = LIVE_RECONNECT_INTERVAL;

  private reconnectTimeoutId: ReturnType<typeof setTimeout> | undefined;

  private lastMessageAt = 0;

  private connectionStatusCheckIntervalId:
    | ReturnType<typeof setInterval>
    | undefined;

  private connectionStatusIntervalMs = 2 * LIVE_RECONNECT_INTERVAL;

  private isLiveNow() {
    if (!this.config) return false;
    const now = Date.now() / 1000;
    return now >= this.config.startDate && now <= this.config.endDate;
  }

  setupLeagueSocket(dispatch: Dispatch<Action>, config: ILeagueWSConfig) {
    if (
      this.config &&
      this.config.leagueId === config.leagueId &&
      this.config.matchId === config.matchId &&
      (this.connectionState === 'OPEN' || this.connectionState === 'CONNECTING')
    ) {
      return;
    }

    // Close existing connection if config changed
    if (this.socket && this.connectionState !== 'CLOSED') {
      this.closeSocket();
    }

    this.dispatch = dispatch;
    this.config = config;

    this.reconnectInterval = this.isLiveNow()
      ? LIVE_RECONNECT_INTERVAL
      : IDLE_RECONNECT_INTERVAL;

    this.connect();
  }

  private connect() {
    if (!this.config) return;

    const { leagueId, matchId } = this.config;
    const wsUrl = `${API_ORIGIN.replace(
      'http',
      'ws',
    )}/ws/leagues/${leagueId}/matches/${matchId}/live/`;

    this.connectionState = 'CONNECTING';
    this.dispatch?.(leagueActions.setWSConnectionState('CONNECTING'));

    try {
      this.socket = new WebSocket(wsUrl);
      this.setupEventHandlers();

      if (!this.isLiveNow()) {
        this.forceCloseForReconnect();
        return;
      }
    } catch (error) {
      this.handleReconnect();
    }
  }

  private setupEventHandlers() {
    if (!this.socket) return;

    this.socket.onopen = () => {
      console.info('League WebSocket connected');
      this.connectionState = 'OPEN';
      this.dispatch?.(leagueActions.setWSConnectionState('OPEN'));
      this.lastMessageAt = Date.now();
      this.startConnectionMonitoring();
      this.onOpenCallback?.();
    };

    this.socket.onmessage = (event) => {
      try {
        const message: IncomingLeagueWSMessage = JSON.parse(event.data);
        this.lastMessageAt = Date.now();
        this.handleMessage(message);
      } catch (error) {
        console.error('LeagueWebSocketManager: Failed to parse message', error);
      }
    };

    this.socket.onerror = (error) => {
      console.error('League WebSocket error:', error);
      this.forceCloseForReconnect();
    };

    this.socket.onclose = () => {
      console.info('League WebSocket disconnected');
      this.connectionState = 'CLOSED';
      this.dispatch?.(leagueActions.setWSConnectionState('CLOSED'));
      this.stopConnectionMonitoring();
      this.handleReconnect();
    };
  }

  private startConnectionMonitoring() {
    this.stopConnectionMonitoring();
    this.connectionStatusCheckIntervalId = setInterval(() => {
      const idleMs = Date.now() - this.lastMessageAt;
      if (idleMs >= this.connectionStatusIntervalMs) {
        this.forceCloseForReconnect();
      }
    }, 2000);
  }

  private stopConnectionMonitoring() {
    if (this.connectionStatusCheckIntervalId) {
      clearInterval(this.connectionStatusCheckIntervalId);
      this.connectionStatusCheckIntervalId = undefined;
    }
  }

  private forceCloseForReconnect() {
    if (
      this.connectionState === 'CLOSING' ||
      this.connectionState === 'CLOSED'
    ) {
      return;
    }
    this.stopConnectionMonitoring();

    if (this.socket) {
      this.socket.close();
      this.socket = undefined;
    }

    this.connectionState = 'CLOSED';
    this.dispatch?.(leagueActions.setWSConnectionState('CLOSED'));
    this.handleReconnect();
  }

  private handleMessage(message: IncomingLeagueWSMessage) {
    console.info(`League WS received: ${JSON.stringify(message)}`);
    if (message.type === 'heartbeat') return;
    this.onMessageCallback?.();
  }

  private handleReconnect() {
    clearTimeout(this.reconnectTimeoutId);

    this.reconnectInterval = this.isLiveNow()
      ? LIVE_RECONNECT_INTERVAL
      : IDLE_RECONNECT_INTERVAL;

    this.reconnectTimeoutId = setTimeout(() => {
      if (this.connectionState === 'CLOSED' && this.config) {
        this.connect();
      }
    }, this.reconnectInterval);
  }

  closeSocket() {
    this.connectionState = 'CLOSING';

    if (this.reconnectTimeoutId) {
      clearTimeout(this.reconnectTimeoutId);
      this.reconnectTimeoutId = undefined;
    }

    this.stopConnectionMonitoring();

    if (this.socket) {
      this.socket.close();
      this.socket = undefined;
    }

    this.connectionState = 'CLOSED';
    this.config = undefined;
    this.dispatch?.(leagueActions.setWSConnectionState('CLOSED'));
  }

  isConnected(): boolean {
    return this.connectionState === 'OPEN';
  }

  private onMessageCallback: (() => void) | null = null;

  setOnMessage(cb: () => void) {
    this.onMessageCallback = cb;
  }

  private onOpenCallback: (() => void) | null = null;

  setOnOpen(cb: () => void) {
    this.onOpenCallback = cb;
  }
}

const leagueWSManager = new LeagueWebSocketManager();
export default leagueWSManager;
